'use client'
import Image from 'next/image'
import { useLocale, useTranslations } from 'next-intl'
import { usePublicUserProfileQuery, useUserQuery } from 'codegen/generated/user'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { decodeHtmlEntities } from 'utils/decode-html-entities'
import { IconPhotoPrints } from '@repo/design-system-cf/components/icons'
import { Button } from '@repo/design-system-cf/components/button'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import { Link } from '@repo/web-ui/components/link'
import { formatUserName } from 'utils/format-user-name'
import { useSocialLatestDownloadImagesQuery } from '@web/gen/crafter'
import { tw } from 'utils/classnames'
import { useCreatePostProjectDialog } from '../../../masterfeed/_components/create-post-project-dialog/create-post-project-dialog-context'

export function NoPosts({ slug }: { slug: string }) {
  const t = useTranslations('social-feed.page.crafter_profile')
  const locale = useLocale()
  const { openDialog } = useCreatePostProjectDialog()
  const { data: publicUserProfileData } = usePublicUserProfileQuery({
    slug: slug,
  })
  const { data: user } = useUserQuery(undefined, {
    select: data => data?.me?.user,
  })
  const { data } = useSocialLatestDownloadImagesQuery(undefined, {
    select: data => data.socialLatestDownloadImages?.images || [],
  })

  const images = data || []

  const isOwn = user?.slug === slug
  const isAllAccess = !!user?.subscriptionAccess.allAccess.isSubscribed

  const userFullName = formatUserName(
    publicUserProfileData?.publicUserProfile.firstName,
    publicUserProfileData?.publicUserProfile.lastName,
    t('metadata.user_title'),
    decodeHtmlEntities,
  )

  // oxlint-disable-next-line no-nested-ternary
  return isOwn ? (
    isAllAccess ? (
      <div className="border-cf-transparent-grey-1000-12 w-full rounded-[12px] border bg-cf-white sm:flex-row p-4 sm:p-0 flex flex-1 h-full sm:justify-between relative flex-col-reverse items-center sm:items-start">
        <div className="flex-1 w-full sm:p-4 sm:pr-0 max-w-[644px]">
          <p className="sm:text-18 text-16 font-semibold sm:font-bold leading-[18px] text-cf-grey-1000 w-full pt-4 pb-2 sm:pb-0 sm:pt-0">
            You {t('no_posts_own_user_text')}
          </p>
          <p className="text-14 text-cf-grey-600 pt-1 pb-4">{t('no_posts_own_text_no_access')}</p>
          <Button variant="primary" className="sm:w-auto self-start w-full" onClick={openDialog}>
            {t('post_project')}
          </Button>
        </div>

        {images.length > 0 ? (
          <div className="xl:hidden flex-1 relative flex gap-4 sm:items-end sm:justify-end sm:mt-auto sm:self-end sm:pr-4">
            <div className="sm:h-full flex items-center ">
              <section
                className={tw(
                  'relative w-[232px] h-[175px] overflow-hidden',
                  images.length === 0 ? '' : 'sm:-mt-8 -mt-12',
                  images.length === 1 ? 'h-[116px]' : '',
                  images.length === 2 ? 'h-[136px]' : '',
                )}
              >
                <div className="relative w-full h-full">
                  <Image
                    src={images[0] || ''}
                    data-testid="post-project-section-image-1"
                    alt={'Feature image 1'}
                    width={200}
                    height={150}
                    className={tw(
                      'absolute top-6 left-6 rounded-[12px] object-cover object-top shadow-md -rotate-6 w-[200px] border-[#1313151F] border bg-cf-white',
                      images.length === 1 ? 'left-1/2 -translate-x-1/2' : '',
                    )}
                  />
                  {images[1] ? (
                    <Image
                      src={images[1] || ''}
                      data-testid="post-project-section-image-2"
                      alt={'Feature image 2'}
                      width={200}
                      height={150}
                      className="absolute top-10 right-2 rounded-[12px] object-cover object-top shadow-md rotate-3 w-[200px] border-[#1313151F] border bg-cf-white"
                    />
                  ) : null}
                  {images[2] ? (
                    <Image
                      src={images[2] || ''}
                      data-testid="post-project-section-image-3"
                      alt={'Feature image 3'}
                      width={200}
                      height={150}
                      className="absolute top-14 left-2 rounded-[12px] object-cover object-top shadow-md -rotate-[3deg] w-[200px] border-[#1313151F] border bg-cf-white"
                    />
                  ) : null}
                </div>
              </section>
            </div>
          </div>
        ) : null}
      </div>
    ) : (
      <div className="border-cf-transparent-grey-1000-12 w-full rounded-[12px] border bg-cf-white sm:flex-row p-4 flex-1 h-full">
        <IconPhotoPrints className="size-12 text-cf-purple-900 mb-3" />
        <p className="sm:text-20 text-16 font-semibold sm:font-bold leading-[18px] text-cf-grey-1000 w-full">
          You {t('no_posts_own_user_text')}
        </p>
        <p className="my-3 text-cf-grey-800 text-16">{t('right_side_text')}</p>
        <div className="flex flex-col gap-3 mb-4">
          <div className="flex gap-2 text-14">
            <div className="text-cf-grey-800 bg-cf-grey-100 rounded-full w-6 h-6 inline-flex items-center justify-center font-bold tabular-nums leading-none shrink-0">
              1.
            </div>
            <div className="text-cf-grey-800">{t('download_step_1')}</div>
          </div>
          <div className="flex gap-2 text-14">
            <div className="text-cf-grey-800 bg-cf-grey-100 rounded-full w-6 h-6 inline-flex items-center justify-center font-bold tabular-nums leading-none shrink-0">
              2.
            </div>
            <div className="text-cf-grey-800">{t('download_step_2')}</div>
          </div>
          <div className="flex gap-2 text-14">
            <div className="text-cf-grey-800 bg-cf-grey-100 rounded-full w-6 h-6 inline-flex items-center justify-center font-bold tabular-nums leading-none shrink-0">
              3.
            </div>
            <div className="text-cf-grey-800">{t('download_step_3')}</div>
          </div>
        </div>
        <Button asChild={true}>
          <Link
            href={addOrReplaceLocaleInUrl(AppRoutes.LICENSE_KEYS_OVERVIEW, locale)}
            target="_blank"
          >
            {t('go_my_downloads')}
          </Link>
        </Button>
      </div>
    )
  ) : (
    <div className="border-cf-transparent-grey-1000-12 bg-cf-white w-full rounded-[12px] border p-4 sm:p-6 flex flex-col justify-center items-center gap-4 flex-1 h-full">
      <IconPhotoPrints className="text-cf-purple-900 size-10" />
      <p className="text-16 sm:text-20 px-4 font-bold leading-[18px] text-cf-grey-1000">
        {userFullName} {t('no_posts_user_text')}
      </p>
    </div>
  )
}
